/**
 * useStudentHomeData — les lectures de l'accueil de l'élève. La mise en forme est au view-model.
 *
 *  - le compte (`getUser`) : la coquille le lit au montage (`BottomTabsNavigator`) ; l'accueil le
 *    relit quand on revient sur l'onglet, pas au premier passage (`TodoListScreen.js:150-163`, pour
 *    les données restées d'une veille), sous la garde partagée avec Activités
 *    (`useAccountRereadOnReturn` : une lecture en vol ou partie il y a moins de 30 s suffit) ;
 *  - les séances (`getAllMyNextLiveSessions`) : la carte du live, les lignes des lives suivants, et
 *    le paiement en attente ;
 *  - les contrôles à venir (`getSchoolAssessments`, `upcoming`) pour le prochain contrôle, une fois
 *    par élève (`NextAssessmentWidget.js`).
 *
 * Il suit aussi le PREMIER AFFICHAGE : quelles lectures de ce montage sont revenues, et si le délai
 * de la lecture secondaire est passé. La règle est au view-model (`studentHomeFirstDisplayLoading`) ;
 * le crochet rend `firstDisplayLoading`, que l'écran donne à la page.
 *
 * `refresh` relance les trois, ce que faisait le « tirer pour rafraîchir » legacy.
 */
import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { liveService } from "../../../../../../data/services/liveService"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import {
    HOME_SECONDARY_HOLD_MS,
    studentHomeFirstDisplayLoading,
    type StudentHomeViewModel,
} from "../../../../view-models/student/Home/studentHomeViewModel"
import { useAccountRereadOnReturn } from "../useAccountRereadOnReturn"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const selectAccountFailed = (state: any): boolean => state?.api?.getUser?.error != null && !state?.api?.getUser?.request

export function useStudentHomeData(vm: StudentHomeViewModel) {
    const dispatch = useDispatch()
    const { studentId } = vm
    const [livesAnswered, setLivesAnswered] = useState(false)
    const [assessmentsAnswered, setAssessmentsAnswered] = useState(false)
    const [secondaryHoldElapsed, setSecondaryHoldElapsed] = useState(false)
    const accountFailed = useSelector(selectAccountFailed)

    const fetchLives = useCallback(() => {
        dispatch(
            apiRequest(liveService.getAllMyNextLiveSessions, {}, {}, {}, null, null, {
                offlineDialog: true,
                onResult: () => setLivesAnswered(true),
            }) as never
        )
    }, [dispatch])

    const fetchAssessments = useCallback(() => {
        if (studentId == null) return
        dispatch(
            apiRequest(
                schoolAssessmentsService.getSchoolAssessments,
                {},
                {},
                { student_id: studentId, upcoming: true },
                null,
                null,
                { offlineDialog: true, onResult: () => setAssessmentsAnswered(true) }
            ) as never
        )
    }, [dispatch, studentId])

    useEffect(() => {
        fetchLives()
    }, [fetchLives])

    useEffect(() => {
        fetchAssessments()
    }, [fetchAssessments])

    useEffect(() => {
        const timer = setTimeout(() => setSecondaryHoldElapsed(true), HOME_SECONDARY_HOLD_MS)
        return () => clearTimeout(timer)
    }, [])

    useAccountRereadOnReturn()

    const refresh = useCallback(() => {
        dispatch(apiRequest(authService.getUser) as never)
        fetchLives()
        fetchAssessments()
    }, [dispatch, fetchAssessments, fetchLives])

    const firstDisplayLoading = studentHomeFirstDisplayLoading({
        accountMissing: vm.loading,
        accountServed: vm.firstDisplay.accountServed,
        accountFailed,
        livesArrived: livesAnswered || vm.firstDisplay.livesKept,
        assessmentsArrived: assessmentsAnswered || vm.firstDisplay.assessmentsKept,
        secondaryHoldElapsed,
    })

    return { refresh, firstDisplayLoading }
}
